import React, { useEffect, useState } from "react";
import { api } from "../../api/apiClient";
import { notify } from "../components-ui/toast";
import { PartySummaryDto } from "../types/types";

interface PartySummaryProps {
  partyId: number;
  summary?: PartySummaryDto;
  embedded?: boolean;
  cards?: boolean;
  showPersonalDetails?: boolean;
}

export default function PartySummary({ partyId, summary: suppliedSummary, embedded = false, cards = false, showPersonalDetails = true }: PartySummaryProps) {
  const [summary, setSummary] = useState<PartySummaryDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (suppliedSummary) {
      setSummary(suppliedSummary);
      return;
    }
    if (!partyId) return;

    setLoading(true);

    api<PartySummaryDto>(`/api/partyquery/${partyId}/summary`)
      .then(setSummary)
      .catch(() => notify.error("Failed to load party details"))
      .finally(() => setLoading(false));
  }, [partyId, suppliedSummary]);

  if (loading) return <p>Loading party details...</p>;
  if (!summary) return null;

  if (cards) return <>
    {showPersonalDetails && <section className="card" aria-label="Personal details">
      <div className="card-header"><div className="card-title">Personal Details</div></div>
      <div className="form-row"><div className="form-group form-group--full">
        <div className="form-label">Name</div><div className="form-input">{summary.displayName}</div>
      </div></div>
    </section>}
    <section className="card" aria-label="Contact details">
      <div className="card-header"><div className="card-title">Contact Details</div></div>
      <div className="form-row">
        {summary.contactMethods.length ? summary.contactMethods.map((contact, index) => <div className="form-group" key={index}>
          <div className="form-label">{contact.contactType}</div><div className="form-input">{contact.value}</div>
        </div>) : <p className="field-hint">No contact details recorded.</p>}
      </div>
    </section>
    <section className="card" aria-label="Address details">
      <div className="card-header"><div className="card-title">Address Details</div></div>
      <div className="form-row"><div className="form-group form-group--full">
        <div className="form-label">Address</div><div className="form-input">{summary.primaryAddress || "Not recorded"}</div>
      </div></div>
    </section>
  </>;

  return (
    <div className={embedded ? "party-summary-content" : "summary"}>
      {!embedded && <div className="summary-title">Party Details</div>}

      <div className="summary-section">
        <div className="summary-name">Name</div>
        <div className="summary-value">{summary.displayName}</div>
      </div>

      <div className="summary-section">
        <div className="summary-name">Address</div>
        <div className="summary-value">
          {summary.primaryAddress || "Not recorded"}
        </div>
      </div>

      {summary.contactMethods.map((contact, index) => (
        <div key={index} className="summary-section">
          <div className="summary-name">{contact.contactType}</div>
          <div className="summary-value">{contact.value}</div>
        </div>
      ))}
    </div>
  );
}
